import { useContext, useEffect, useState } from 'react';
import axios from "axios";
import Moment from 'react-moment';
import { Badge, Button, Form as BootstrapForm, ListGroup, Modal } from "react-bootstrap";
import { SystemErrorContext } from '../components/SystemError';

const STATUS_VARIANT = {
  open: 'warning',
  approved: 'success',
  denied: 'danger',
};

// Staff-facing list of claims submitted against a single animal, with deny/approve actions.
const AnimalClaimsList = ({ animalId, onApprove }) => {

  const { setShowSystemError } = useContext(SystemErrorContext);

  const [claims, setClaims] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [denyTarget, setDenyTarget] = useState(null);
  const [denyNote, setDenyNote] = useState('');
  const [denyError, setDenyError] = useState('');
  const [approveTarget, setApproveTarget] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchClaims = () => {
    setIsLoading(true);
    axios.get('/reunification/api/claim/?animal=' + animalId)
      .then(response => {
        setClaims(response.data);
      })
      .catch(() => {
        setShowSystemError(true);
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    if (animalId) {
      fetchClaims();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animalId]);

  const handleDeny = () => {
    if (!denyNote.trim()) {
      setDenyError('A note is required to deny a claim.');
      return;
    }
    setIsSubmitting(true);
    axios.put('/reunification/api/claim/' + denyTarget.id + '/deny/', { review_note: denyNote })
      .then(() => {
        setDenyTarget(null);
        setDenyNote('');
        setDenyError('');
        fetchClaims();
      })
      .catch(() => {
        setShowSystemError(true);
      })
      .finally(() => setIsSubmitting(false));
  };

  const handleApprove = () => {
    setIsSubmitting(true);
    axios.put('/reunification/api/claim/' + approveTarget.id + '/approve/')
      .then(() => {
        setApproveTarget(null);
        fetchClaims();
        if (onApprove) {
          onApprove();
        }
      })
      .catch(() => {
        setShowSystemError(true);
      })
      .finally(() => setIsSubmitting(false));
  };

  if (isLoading) {
    return <p>Loading claims...</p>;
  }

  return (
    <>
      <ListGroup variant="flush">
        {claims.map(claim => (
          <ListGroup.Item key={claim.id} className="mt-2">
            <div className="d-flex justify-content-between align-items-start">
              <div style={{marginTop:"-10px"}}>
                <div>
                  <b>{claim.first_name} {claim.last_name}</b>{' '}
                  <Badge bg={STATUS_VARIANT[claim.status]} className="text-capitalize">{claim.status}</Badge>
                </div>
                <div><b>Phone: </b>{claim.display_phone || 'N/A'} {claim.alt_phone ? '(Alt: ' + claim.alt_phone + ')' : ''}</div>
                <div><b>Email: </b>{claim.email || 'N/A'}</div>
                <div><b>Address: </b>{[claim.address, claim.city, claim.state, claim.zip_code].filter(Boolean).join(', ') || 'N/A'}</div>
                <div><b>Opened: </b><Moment format="MM/DD/YYYY HH:mm">{claim.created_at}</Moment></div>
                {claim.notes ? <div><b>Notes: </b>{claim.notes}</div> : ""}
                {claim.status !== 'open' ? <div className="fst-italic"><b>Review Note: </b>{claim.review_note}</div> : ""}
              </div>
              {claim.status === 'open' ? (
                <div className="text-nowrap ms-3">
                  <Button variant="danger" className="float-end border" style={{ marginTop: "-7px", marginBottom: "-8px", marginRight: "-15px" }} onClick={() => { setDenyTarget(claim); setDenyNote(''); setDenyError(''); }}>Deny</Button>
                  <Button className="float-end border" style={{ marginTop: "-7px", marginBottom: "-8px" }} onClick={() => setApproveTarget(claim)}>Approve</Button>
                </div>
              ) : ""}
            </div>
          </ListGroup.Item>
        ))}
        {claims.length === 0 ? <ListGroup.Item>No claims have been submitted for this animal.</ListGroup.Item> : ""}
      </ListGroup>
      <Modal show={!!denyTarget} onHide={() => setDenyTarget(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Deny Claim</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <BootstrapForm.Group>
            <BootstrapForm.Label>A note is required to deny this claim.</BootstrapForm.Label>
            <BootstrapForm.Control as="textarea" rows={3} value={denyNote} onChange={e => { setDenyNote(e.target.value); setDenyError(''); }} isInvalid={!!denyError} />
            <BootstrapForm.Control.Feedback type="invalid">{denyError}</BootstrapForm.Control.Feedback>
          </BootstrapForm.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="danger" onClick={handleDeny} disabled={isSubmitting}>{isSubmitting ? "Denying..." : "Deny Claim"}</Button>
          <Button variant="secondary" onClick={() => setDenyTarget(null)}>Cancel</Button>
        </Modal.Footer>
      </Modal>
      <Modal show={!!approveTarget} onHide={() => setApproveTarget(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Approve Claim</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Approving this claim will add {approveTarget ? approveTarget.first_name + ' ' + approveTarget.last_name : 'this person'} as
            an owner of this animal, mark the animal as no longer public, and automatically deny all other open claims on this animal.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleApprove} disabled={isSubmitting}>{isSubmitting ? "Approving..." : "Approve Claim"}</Button>
          <Button variant="secondary" onClick={() => setApproveTarget(null)}>Cancel</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AnimalClaimsList;
